import { ReactNode, createContext, useContext, useState, useEffect } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import axios from 'axios';
import { registrationContext, IRegistration } from '../../RegistrationContext';

interface IEditorContext {
    isOpen: boolean
    isSaving: boolean
    receiptUploaded: boolean
    isUploading: boolean
    isUploaded: boolean
    updateRequest: UpdateRequest
    updateSchema: any
    legalMonths: number

    updateRegistration: (values: UpdateRequest) => Promise<void>
    uploadReceipt: (file: any) => Promise<void>
    open: () => void
    close: () => void
}

let updateSchema = yup.object({
    registrationId: yup.string().default('').required(),
    purchaseDate: yup.date().default(moment().toDate()).required(),
    extensionMonths: yup.number().default(0),
    serialNumber: yup.string().nullable().default(''),
    reason: yup.string().default(''),
    receiptId: yup.string().nullable().default(''),
    receiptFileName: yup.string().nullable().default(''),
    unlimited: yup.boolean().default(false)
})

type UpdateRequest = yup.InferType<typeof updateSchema>;

export let editorContext = createContext({} as IEditorContext);

let { Provider } = editorContext;

let EditorProvider: React.FC<{ children: ReactNode, registration: IRegistration }> = ({ children, registration }) => {
    let service = useContext(registrationContext);

    let [isOpen, setOpen] = useState(false);
    let [isSaving, setSaving] = useState(false);
    let [receiptUploaded, setReceiptUploaded] = useState(false);
    let [isUploading, setUploading] = useState(false);
    let [isUploaded, setUploaded] = useState(false);
    let [updateRequest, setUpdateRequest] = useState<UpdateRequest>(updateSchema.getDefault());


    useEffect(() => {
        if (isOpen === true) {
            let request = {
                ...updateRequest,
                extensionMonths: registration.extensionMonths - registration.legalMonths,
                purchaseDate: moment(registration.purchaseDate).toDate(),
                registrationId: registration.registrationId,
                serialNumber: registration.serialNumber || updateSchema.getDefault().serialNumber,
                unlimited: registration.unlimited || updateSchema.getDefault().unlimited
            };

            if (registration.receiptId)
                setReceiptUploaded(true);

            setUpdateRequest(request);
        }
    }, [isOpen])


    let open = () => {
        setOpen(true);
    }

    let close = () => {
        setOpen(false);
        setSaving(false);
    }

    let updateRegistration = async (values: UpdateRequest) => {
        try {
            setSaving(true);
            let res = await axios.post('/api/Product/UpdateRegistration', values);
            service.updateRegistration(res.data.item);
            close();

        } catch (err) {
            setSaving(false);
        }
    }

    let uploadReceipt = async (file: any) => {
        try {
            setUploading(true);
            setUploaded(false);

            let formData = new FormData();
            formData.append('file', file);

            let res = await axios.post('/api/Product/UploadReceipt', formData);
            setUpdateRequest({
                ...updateRequest,
                receiptId: res.data.id,
                receiptFileName: file.name
            });

            setUploaded(true);

        } catch (err) { }
        finally {
            setUploading(false);
        }
    }


    return (
        <Provider
            value={{
                isOpen,
                isSaving,
                receiptUploaded,
                isUploading,
                isUploaded,
                updateRequest,
                updateSchema,
                legalMonths: registration.legalMonths,

                updateRegistration,
                uploadReceipt,
                open,
                close
            }}
        >
            {children}
        </Provider>
    )
}

export default EditorProvider;